import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useScroll } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { WHATSAPP_URL, APP_URL, WhatsAppIcon } from "./shared";

const LINKS = [
  { label: "Plataforma", to: "/", testId: "nav-link-plataforma" },
  { label: "Método", to: "/metodo", testId: "nav-link-metodo" },
  { label: "Como funciona", to: "/como-funciona", testId: "nav-link-como-funciona" },
  { label: "Diferenciais", to: "/diferenciais", testId: "nav-link-diferenciais" },
  { label: "Para quem é", to: "/para-quem", testId: "nav-link-para-quem" },
];

const linkCls = (isActive) =>
  `group relative text-[13.5px] font-medium transition-colors ${isActive ? "text-white" : "text-tria-mist hover:text-white"}`;

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { scrollYProgress } = useScroll();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goHome = () => {
    navigate("/");
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  };

  return (
    <>
      <motion.div
        className="fixed inset-x-0 top-0 z-[80] h-[2px] origin-left bg-gradient-to-r from-tria-sky via-tria-blue to-tria-vivid"
        style={{ scaleX: scrollYProgress }}
      />
      <header
        data-testid="main-nav"
        className={`fixed inset-x-0 top-0 z-[75] transition-all duration-500 ${
          scrolled ? "border-b border-white/[0.07] bg-tria-navy/80 backdrop-blur-xl" : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
          <button
            data-testid="nav-logo-btn"
            onClick={goHome}
            className="transition-opacity hover:opacity-80"
            aria-label="Ir para a página inicial"
          >
            <Logo compact />
          </button>

          <nav className="hidden items-center gap-8 lg:flex">
            {LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.to === "/"} data-testid={l.testId} className={({ isActive }) => linkCls(isActive)}>
                {({ isActive }) => (
                  <>
                    {l.label}
                    <span className={`absolute -bottom-1.5 left-0 h-px bg-tria-sky transition-all duration-300 ${isActive ? "w-full" : "w-0 group-hover:w-full"}`} />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            <a
              data-testid="nav-signup-btn"
              href={APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-1.5 rounded-full bg-white px-5 py-2.5 text-[13.5px] font-bold text-tria-navy transition-all duration-300 hover:scale-[1.03] hover:bg-tria-sky"
            >
              Criar conta
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              data-testid="nav-demo-btn"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="glow-blue inline-flex items-center gap-2 rounded-full bg-tria-vivid px-5 py-2.5 text-[13.5px] font-bold text-white transition-all duration-300 hover:bg-tria-blue"
            >
              <WhatsAppIcon className="h-3.5 w-3.5" />
              Agendar demonstração
            </a>
          </div>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="rounded-lg p-2 text-white lg:hidden"
            aria-label="Abrir menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden border-b border-white/[0.07] bg-tria-navy/95 backdrop-blur-xl lg:hidden"
            >
              <div className="flex flex-col gap-1 px-6 py-6">
                {LINKS.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    end={l.to === "/"}
                    data-testid={`${l.testId}-mobile`}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `rounded-lg px-3 py-3 text-left text-[15px] font-medium transition-colors hover:bg-white/5 ${isActive ? "text-white" : "text-tria-mist hover:text-white"}`
                    }
                  >
                    {l.label}
                  </NavLink>
                ))}
                <div className="mt-4 flex flex-col gap-3 border-t border-white/10 pt-5">
                  <a data-testid="nav-signup-btn-mobile" href={APP_URL} target="_blank" rel="noopener noreferrer" className="rounded-full bg-white px-5 py-3 text-center text-sm font-bold text-tria-navy">
                    Criar conta
                  </a>
                  <a data-testid="nav-demo-btn-mobile" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 rounded-full bg-tria-vivid px-5 py-3 text-sm font-bold text-white">
                    <WhatsAppIcon className="h-4 w-4" />
                    Agendar demonstração
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
};
